import { validSearchTerm } from "./searchTermPrivacy";
import {
  generatedProductEventSchemaVersions,
  generatedProductEventSchemas,
  type GeneratedProductEventName,
  type GeneratedProductEventRuntimePropertySchema,
} from "./productEventContract.generated";
import {
  generatedWebProductEventNames,
  generatedWebProductEvents,
  type GeneratedWebProductEvent,
} from "./productEventProducer.generated";

export type ProductAnalyticsPlatform = "ios" | "web" | "server";

export interface ProductAnalyticsContext {
  platform: ProductAnalyticsPlatform;
  environment: "development" | "test" | "production";
  appVersion: string;
  buildNumber: string;
  buildChannel: string;
}

export type ProductEvent = GeneratedWebProductEvent;

export { generatedWebProductEvents as productEvents };

export interface ProductEventEnvelope {
  eventId: string;
  occurredAt: string;
  eventName: GeneratedProductEventName;
  eventSchemaVersion: number;
  platform: ProductAnalyticsPlatform;
  environment: ProductAnalyticsContext["environment"];
  appVersion: string;
  buildNumber: string;
  buildChannel: string;
  properties: Record<string, string | boolean>;
}

export interface AnalyticsSink {
  record(envelope: ProductEventEnvelope): void;
}

export interface ProductAnalyticsRecording {
  record(event: ProductEvent): void;
}

const forbiddenValuePatterns = [
  /https?:\/\//i,
  /(?:^|\s)\/users\//i,
  /(?:firebase|sharedlists?|recipes?|groceryitems?|documents?)[/_-]?(?:uid|id|path|key)?[-_:/.\w]*\d/i,
  /@/,
];

const eventSchemas = generatedProductEventSchemas as Record<
  GeneratedProductEventName,
  Record<string, GeneratedProductEventRuntimePropertySchema>
>;

/** Runtime event-name catalogue shared by envelope and schema validation. */
export const productEventNames = Object.freeze(
  Object.keys(eventSchemas) as GeneratedProductEventName[],
);

const areValidProperties = (
  eventName: string,
  properties: unknown,
): properties is Record<string, string | boolean> => {
  const schema = eventSchemas[eventName as GeneratedProductEventName];
  if (
    !schema ||
    !properties ||
    typeof properties !== "object" ||
    Array.isArray(properties)
  )
    return false;
  const actualKeys = Object.keys(properties);
  if (actualKeys.some((key) => !(key in schema))) return false;
  if (
    Object.entries(schema).some(
      ([key, propertySchema]) =>
        !propertySchema.optional && !(key in properties),
    )
  )
    return false;
  const propertiesAreValid = Object.entries(properties).every(
    ([key, property]) => {
      const propertySchema = schema[key];
      if (propertySchema?.type === "search_term")
        return validSearchTerm(property);
      if (!propertySchema || typeof property !== propertySchema.type)
        return false;
      if (typeof property === "boolean") return true;
      if (property.length === 0 || property.length > 80) return false;
      if (
        propertySchema.type !== "string" ||
        !propertySchema.values.includes(property)
      )
        return false;
      return !forbiddenValuePatterns.some((pattern) => pattern.test(property));
    },
  );
  if (!propertiesAreValid) return false;

  if (eventName === "cooking_timer_acknowledged") {
    const acknowledgement = properties as Record<string, unknown>;
    const hasExtensionKind = "extension_kind" in acknowledgement;
    return (acknowledgement.action === "add_time") === hasExtensionKind;
  }
  return true;
};

const isValidEvent = (value: ProductEvent): boolean =>
  generatedWebProductEventNames.includes(value.name) &&
  value.schemaVersion === generatedProductEventSchemaVersions[value.name] &&
  areValidProperties(value.name, value.properties);

const eventIdPattern =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const appVersionPattern =
  /^[0-9]+(?:\.[0-9]+){0,3}(?:[-+][A-Za-z0-9.-]{1,16})?$/;
const buildNumberPattern = /^[0-9]+(?:\.[0-9]+){0,2}$/;
const buildChannelPattern = /^[a-z][a-z0-9_]{0,31}$/;
const earliestPlausibleOccurrence = Date.parse("2020-01-01T00:00:00.000Z");
const maximumFutureSkewMilliseconds = 24 * 60 * 60 * 1_000;
const identifierShapedValuePatterns = [
  /https?:\/\//i,
  /@/,
  /[\\/]/,
  /(?:^|_)(?:firebase_?)?u(?:ser)?id(?:_|$)/i,
  /(?:^|_)(?:recipe|list|document|shared_list)_?id(?:_|$)/i,
  /^[A-Za-z0-9_-]{28,}$/,
  /^[0-9a-f]{8}-[0-9a-f-]{27,}$/i,
];
const envelopeKeys = [
  "appVersion",
  "buildChannel",
  "buildNumber",
  "environment",
  "eventId",
  "eventName",
  "eventSchemaVersion",
  "occurredAt",
  "platform",
  "properties",
].sort();

const isSafeContextValue = (value: string): boolean =>
  !identifierShapedValuePatterns.some((pattern) => pattern.test(value));

const isValidOccurredAt = (value: string, currentTime: number): boolean => {
  if (typeof value !== "string") return false;
  const occurredAt = Date.parse(value);
  return (
    Number.isFinite(occurredAt) &&
    new Date(occurredAt).toISOString() === value &&
    occurredAt >= earliestPlausibleOccurrence &&
    occurredAt <= currentTime + maximumFutureSkewMilliseconds
  );
};

/** Central provider-neutral boundary applied before any sink is invoked. */
export const isValidProductEventEnvelope = (
  value: ProductEventEnvelope,
  currentTime = Date.now(),
): boolean =>
  Boolean(value) &&
  typeof value === "object" &&
  Object.keys(value)
    .sort()
    .every((key, index) => key === envelopeKeys[index]) &&
  Object.keys(value).length === envelopeKeys.length &&
  eventIdPattern.test(value.eventId) &&
  isValidOccurredAt(value.occurredAt, currentTime) &&
  productEventNames.includes(value.eventName) &&
  value.eventSchemaVersion ===
    generatedProductEventSchemaVersions[value.eventName] &&
  (value.platform === "ios" ||
    value.platform === "web" ||
    value.platform === "server") &&
  (value.environment === "development" ||
    value.environment === "test" ||
    value.environment === "production") &&
  typeof value.appVersion === "string" &&
  value.appVersion.length <= 32 &&
  (value.appVersion === "unavailable" ||
    appVersionPattern.test(value.appVersion)) &&
  isSafeContextValue(value.appVersion) &&
  typeof value.buildNumber === "string" &&
  value.buildNumber.length <= 20 &&
  (value.buildNumber === "unavailable" ||
    buildNumberPattern.test(value.buildNumber)) &&
  isSafeContextValue(value.buildNumber) &&
  typeof value.buildChannel === "string" &&
  buildChannelPattern.test(value.buildChannel) &&
  isSafeContextValue(value.buildChannel) &&
  areValidProperties(value.eventName, value.properties);

export class ProductAnalytics implements ProductAnalyticsRecording {
  constructor(
    private readonly sink: AnalyticsSink,
    private readonly context: ProductAnalyticsContext,
    private readonly dependencies: {
      createEventId: () => string;
      now: () => Date;
    } = {
      createEventId: () => globalThis.crypto.randomUUID(),
      now: () => new Date(),
    },
  ) {}

  record(value: ProductEvent): void {
    try {
      if (!isValidEvent(value)) return;
      const now = this.dependencies.now();
      const envelope: ProductEventEnvelope = {
        eventId: this.dependencies.createEventId(),
        occurredAt: now.toISOString(),
        eventName: value.name,
        eventSchemaVersion: value.schemaVersion,
        ...this.context,
        properties: value.properties,
      };
      if (!isValidProductEventEnvelope(envelope)) return;
      this.sink.record(envelope);
    } catch {
      // Analytics must never alter the product action that attempted to record it.
    }
  }
}

export class RecordingAnalyticsSink implements AnalyticsSink {
  readonly envelopes: ProductEventEnvelope[] = [];

  record(envelope: ProductEventEnvelope): void {
    this.envelopes.push(structuredClone(envelope));
  }
}

export class NoopAnalyticsSink implements AnalyticsSink {
  record(): void {}
}
